import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { LOGO_URL } from '../../mock';

export default function SetupPage() {
  const { admin, setupNeeded, register, loading } = useAdminAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (!setupNeeded && !admin) return <Navigate to="/admin/login" replace />;
  if (admin) return <Navigate to="/admin" replace />;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success('Admin account created!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0d12] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute -top-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#5DCAB5]/12 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(229,97,76,0.6)] overflow-hidden">
            <img src={LOGO_URL} alt="GEMS" className="h-full w-full object-contain p-1.5" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-[22px] font-bold">
              GEMS <span className="text-[#E5614C] italic">@</span> The Arts
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">Admin CMS</div>
          </div>
        </div>

        <div className="bg-[#16131a] border border-white/[0.06] rounded-[24px] p-8 lg:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5DCAB5]/15 text-[#5DCAB5] text-[11px] font-semibold uppercase tracking-[0.18em]">
            <ShieldCheck className="h-3.5 w-3.5" /> First-Time Setup
          </div>
          <h1 className="font-serif-display text-[28px] font-bold mt-4">Create your admin account</h1>
          <p className="text-[14px] text-white/55 mt-1">This becomes the primary admin for the CMS.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-white/80 text-[13px] font-semibold">Your Name</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  value={form.name} onChange={(e) => update('name', e.target.value)}
                  placeholder="Your full name" required
                  className="h-12 pl-10 rounded-xl bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80 text-[13px] font-semibold">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  placeholder="admin@gemsatthearts.org" required
                  className="h-12 pl-10 rounded-xl bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80 text-[13px] font-semibold">Password (min 8 chars)</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  type="password" value={form.password} onChange={(e) => update('password', e.target.value)}
                  placeholder="Choose a strong password" required minLength={8}
                  className="h-12 pl-10 rounded-xl bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                />
              </div>
            </div>
            <Button
              type="submit" disabled={busy}
              className="w-full h-12 rounded-xl bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold py-3 mt-2 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create Admin Account <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
